/**
 * Goal Game — the fan screen, as a PURE view.
 *
 * Every pixel a fan sees lives here and nowhere else. `GoalFan.jsx` owns the
 * polling, the PIN calls and the scratch call and hands the result down; the
 * control page's phone preview hands down made-up state instead. Neither one
 * keeps a copy of this markup, so the preview cannot drift from the real thing.
 *
 * THE REMOUNT TRAP, written down because it cost a bug once: `Shell` is
 * declared at MODULE level on purpose. Declared inside a component it would be
 * a NEW component type on every render, React would throw away the subtree, and
 * the fan's scratch progress would be wiped every two seconds.
 *
 * `framed` is the only concession to the preview: inside a phone frame the page
 * fills its box instead of the viewport, because `min-h-screen` and a fixed
 * background attachment both measure the WINDOW, not the frame.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart3, Bell, Coins, Languages, ListOrdered, Loader2, LogOut,
         MessageCircle, Radio, RotateCw, Info, ArrowLeft, MapPin, Star, Trophy, Users } from 'lucide-react';
import { toast } from 'sonner';
import { GoalScratch } from './GoalScratch';
import { GoalBeacon } from './GoalBeacon';
import { GoalCelebrate } from './GoalCelebrate';
import { GoalScoreWatch } from './GoalScoreWatch';
import { GoalStatsPanel } from './GoalStatsPanel';
import { GoalLivePlayersBoard } from './GoalLivePlayersBoard';
import { QRCodeSVG } from 'qrcode.react';
import { GoalPlayerAccount } from './GoalPlayerAccount';
import { saveErrorKey, takenBy } from './goalSaveError';
import { GoalVenueChoice } from './GoalVenueChoice';
import { GoalSponsorLine } from './GoalSponsorLine';
import { GoalEntryBlock } from './GoalEntryBlock';
import { GoalStaffTable } from './GoalStaffTable';
import { CardPicks } from './GoalCardPicks';
import { GoalGoodLuck } from './GoalGoodLuck';
import { BackBar, ByeScreen, LanguageGate, LockConfirm, MenuButton, RosterList,
         TeamDuel } from './GoalGuestFlow';
import { GoalFriendsScreen, GoalHistoryScreen, GoalInviteScreen,
         GoalReminderScreen, GoalWinsScreen, useMyHub } from './GoalMyTapnwin';
import { GOAL_GAME_DEFAULT, bodyFontSize, fontStack, headlineFontSize } from '../../lib/designTokens';
import BrandingShell from '../shared/BrandingShell';
import { GoalTapelligenceFooter } from './GoalTapelligenceFooter';
import { CARD_STYLES } from '../shared/cardSurface';

export const absUrl = (url) => (!url ? null : (url.startsWith('http') ? url : `${(typeof window !== 'undefined' ? window.location.origin : '')}${url}`));

// The language this phone chose on its very first tap, kept per device.
export const LANG_KEY = 'goalGameLang';
// THE PHONE'S OWN MARK. It is what the player's profile is remembered by, so
// it is created here on first use — nothing else on the screen makes one.
const deviceId = () => {
    try {
        let id = localStorage.getItem('patron_device_id');
        if (!id) {
            id = (typeof crypto !== 'undefined' && crypto.randomUUID)
                ? crypto.randomUUID()
                : `dev-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
            localStorage.setItem('patron_device_id', id);
        }
        return id;
    } catch (_e) { return ''; }
};
// The table registration signs the phone in under the GAME's device mark,
// while the Patron Points card uses its own: a pick must be saved with
// whichever of the two this player is actually known by, so a refusal is
// retried once with the other one before it is called a failure.
const gameDeviceId = () => {
    try { return localStorage.getItem('goalGameDeviceId') || ''; } catch (_e) { return ''; }
};
const postAsPlayer = async (url, body) => {
    const send = (device_id) => fetch(url, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...body, device_id }),
    });
    const res = await send(deviceId());
    if (res.status !== 401 && res.status !== 403) return res;
    const alt = gameDeviceId();
    if (!alt || alt === deviceId()) return res;
    const second = await send(alt);
    // The phone is known by that mark, so keep it: from here on everything
    // this player reads and writes uses the same one.
    if (second.ok) {
        try { localStorage.setItem('patron_device_id', alt); } catch (_e) { /* private mode */ }
    }
    return second;
};

/** Is the patron programme on for this card, is this phone known, and — for
 *  the entry block at the top — the screen name, the LIVE balance and the
 *  venue's own program name. All from the SAME endpoint the patron card reads,
 *  so the button's label can never disagree with the card below it. A venue
 *  without the programme answers `enabled: false` and no button is drawn. */
const usePatronBadge = (cardId, lang) => {
    const [badge, setBadge] = useState({ enabled: false, signed_in: false });
    const [stamp, setStamp] = useState(0);
    useEffect(() => {
        if (!cardId) return undefined;
        let alive = true;
        const read = async () => {
            try {
                const device = localStorage.getItem('patron_device_id') || '';
                const res = await fetch(
                    `${(typeof window !== 'undefined' ? window.location.origin : '')}/api/patron/public/${cardId}/me`
                    + `?device_id=${encodeURIComponent(device)}&lang=${lang}`);
                if (!res.ok) return;
                const d = await res.json();
                if (alive) setBadge({
                    enabled: !!d.enabled, signed_in: !!d.signed_in,
                    // The points on screen are always the server's number.
                    points: Number(d.points || 0),
                    screen_name: d.screen_name || '',
                    program_name: (d.settings || {}).program_name || '',
                    consent_text: d.consent_text || '',
                    consent_note: d.consent_note || '',
                    // What this player chose for game reminders, and the
                    // wording beside the SMS tick, both from the server.
                    sms_consent_text: d.sms_consent_text || '',
                    reminder_channel: d.reminder_channel || 'email',
                    reminder_mobile_masked: d.reminder_mobile_masked || '',
                    reminder_sms_consent: !!d.reminder_sms_consent,
                    // THE STAR THIS PLAYER PICKED tonight, so the main page can
                    // name it and stop asking once it is locked in.
                    pick: d.pick || null,
                });
            } catch (_e) { /* the bar simply shows one row fewer */ }
        };
        read();
        const id = setInterval(() => {
            if (document.visibilityState === 'visible') read();
        }, 10000);
        return () => { alive = false; clearInterval(id); };
    }, [cardId, lang, stamp]);
    // Signing in, registering or signing out must not wait for the next poll.
    return [badge, () => setStamp((n) => n + 1)];
};

// A prize picture comes out of image_delivery. The PRE-BAKED variant path is
// the fast one — a fixed `.webp` filename, `immutable` caching, no `Vary`, so
// Cloudflare can hold it at the edge (1.9 KB here against 4.7 KB on the
// on-demand path). If a variant has never been baked the route bakes it on the
// first request, so there is nothing to set up.
const sizedUrl = (url, w) => {
    if (!url) return null;
    const asset = url.match(/^\/api\/brand-assets\/([A-Za-z0-9_-]+)\/serve/);
    if (asset) {
        const ver = url.includes('?') ? url.slice(url.indexOf('?')) : '';
        return `${absUrl(`/api/brand-assets/${asset[1]}/serve/w${w}.webp`)}${ver}`;
    }
    return `${absUrl(url)}${url.includes('?') ? '&' : '?'}w=${w}`;
};

// Colour fallbacks = today's look, so an org with no colours set is unchanged.
// Nothing saved → Red, White and Blue. Panels are tinted with WHITE, never
// with the accent: an amber accent at 15% over a dark background is what made
// the old boxes look muddy brown.
export const palette = (brand) => {
    const c = brand?.colors || {};
    return {
        bg: c.bg || GOAL_GAME_DEFAULT.bg,
        text: c.text || GOAL_GAME_DEFAULT.text,
        accent: c.accent || GOAL_GAME_DEFAULT.accent,
        button: c.button || c.accent || GOAL_GAME_DEFAULT.button,
    };
};

export const typo = (brand) => {
    const d = brand?.design || {};
    // Step × fine slider. Both unset → null → the screen renders as it always has.
    const headline = headlineFontSize(d.headline_size, d.headline_scale);
    const body = bodyFontSize(d.body_size, d.body_scale);
    return {
        headline: {
            fontFamily: fontStack(d.headline_font) || undefined,
            fontSize: headline || undefined,
            lineHeight: headline ? 1.05 : undefined,
        },
        body: {
            fontFamily: fontStack(d.body_font) || undefined,
            fontSize: body || undefined,
        },
    };
};

// Panels: the theme's own surface, with the accent only as a hairline.
const panelStyle = (c) => ({
    backgroundColor: 'rgba(255,255,255,0.07)',
    borderColor: `${c.accent}33`,
});

// ── button style: the same Frosted / Clear / Solid the cards use ───────────
// CARD_STYLES is the shared vocabulary; the surfaces there are white-tinted
// for cards on a wallpaper, so here the SAME three treatments are drawn in the
// sponsor's own button colour. Unset renders the solid button the fan screens
// have always had, so no existing setup changes.
const rgba = (hex, alpha) => {
    const h = String(hex || '').replace('#', '');
    const full = h.length === 3 ? h.split('').map((x) => x + x).join('') : h;
    if (full.length !== 6) return `rgba(255,255,255,${alpha})`;
    const n = parseInt(full, 16);
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
};

const known = (style) => (CARD_STYLES.includes(style) ? style : null);

/** A filled, primary action: Set up my table, Continue, the conference picks. */
export const primarySkin = (style, colour) => {
    switch (known(style)) {
        case 'frosted':
            return { backgroundColor: rgba(colour, 0.35), backdropFilter: 'blur(10px)',
                     WebkitBackdropFilter: 'blur(10px)', border: `1px solid ${rgba(colour, 0.6)}`,
                     color: '#ffffff' };
        case 'clear':
            return { backgroundColor: 'transparent', border: `1px solid ${colour}`, color: colour };
        default:                                    // solid, and unset
            return { backgroundColor: colour, color: '#ffffff' };
    }
};

/** A quiet, outlined action: Check again, Tonight's game, the score bar. */
export const ghostSkin = (style, colour) => {
    switch (known(style)) {
        case 'frosted':
            return { backgroundColor: rgba(colour, 0.18), backdropFilter: 'blur(10px)',
                     WebkitBackdropFilter: 'blur(10px)', border: `1px solid ${rgba(colour, 0.45)}` };
        case 'clear':
            return { backgroundColor: 'transparent', border: `1px solid ${colour}` };
        case 'solid':
            return { backgroundColor: colour, color: '#ffffff', border: `1px solid ${colour}` };
        default:
            return undefined;                       // unset → today's hairline
    }
};

/** The two score-bar buttons, which may each carry their own colour.
 *
 *  Nothing set → exactly today: they follow the general button treatment. A
 *  colour on its own outlines the button, because an outline is what the bar
 *  has always looked like; pick a Button style as well and that style is drawn
 *  in the chosen colour. The bar's own text colour, if set, wins over both. */
export const scoreBarSkin = (design, key, fallback, c) => {
    const colour = design?.[key];
    const text = design?.scorebar_text;
    if (!colour && !text) return fallback;
    const base = colour
        ? (ghostSkin(design?.button_style, colour) || ghostSkin('clear', colour))
        : { ...(fallback || {}) };
    return text ? { ...base, color: text } : base;
};

const Shell = ({ lang, onLang, brand, opBar, footer, framed, scoreWatch, tableLabel,
                 barPad = 'pb-20', children }) => {
    const c = palette(brand);
    const ty = typo(brand);
    const overlay = brand?.design?.wallpaper_overlay;
    const inner = (
        <div className="design-body-scope" style={{ color: c.text, ...ty.body }}>
            <div className="flex gap-1 justify-center mb-6" data-testid="goal-lang-toggle">
                {['en', 'fr', 'es'].map((code) => (
                    <button
                        key={code}
                        onClick={() => onLang(code)}
                        data-testid={`goal-lang-${code}`}
                        style={lang === code ? { backgroundColor: c.button, color: '#ffffff' } : undefined}
                        className={`px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider transition-colors ${
                            lang === code ? '' : 'bg-white/10 text-white/60'
                        }`}
                    >
                        {code}
                    </button>
                ))}
            </div>
            {brand?.logo_url ? (
                <img
                    src={absUrl(brand.logo_url)}
                    alt={brand.organization_name || ''}
                    style={{ height: `${brand.logo_size_px || 48}px` }}
                    className="mx-auto mb-6 object-contain"
                    data-testid="goal-brand-logo"
                />
            ) : brand?.organization_name ? (
                <p className="text-center text-sm font-semibold tracking-wide opacity-70 mb-6"
                   data-testid="goal-brand-name">{brand.organization_name}</p>
            ) : null}
            {/* The table staff named. It stays at the top of every screen, all
                night, so nobody has to remember which table they are. */}
            {tableLabel && (
                <p className="text-center text-base font-bold mb-5 -mt-3"
                   style={{ color: 'rgba(255,255,255,0.92)',
                            textShadow: '0 1px 2px rgba(0,0,0,0.55)' }}
                   data-testid="goal-table-number">
                    {tableLabel}
                </p>
            )}
            {children}
            {footer}
            {/* THE SMALL TAPELLIGENCE LINE. It lives in the shell, so every
                player screen — today's and any added later — carries it, and
                its bottom padding keeps it clear of the button bar and the
                operator bar. */}
            <GoalTapelligenceFooter pad={barPad} />
        </div>
    );

    // A wallpaper gets a darkening wash so white text stays readable on a bright
    // photo. 0 keeps the photo untouched; unset uses a gentle default.
    const wash = (
        <div className="absolute inset-0 pointer-events-none"
             style={{ backgroundColor: `rgba(0,0,0,${(overlay == null ? 45 : overlay) / 100})` }}
             aria-hidden="true" data-testid="goal-wallpaper-wash" />
    );

    const wallpaper = absUrl(brand?.wallpaper_url);

    // Inside the phone frame the whole page is one box: own background, own
    // scroll, no viewport units and no fixed attachment.
    if (framed) {
        return (
            <>
                <div className="min-h-full w-full flex flex-col items-center justify-center px-5 py-8 relative"
                     style={wallpaper
                         ? { backgroundImage: `url("${wallpaper}")`, backgroundSize: 'cover', backgroundPosition: 'center' }
                         : { background: c.bg }}
                     data-testid="goal-fan-page" data-framed="true">
                    {wallpaper && wash}
                    <div className="relative w-full max-w-sm">{inner}</div>
                    {scoreWatch}
                </div>
                {opBar}
            </>
        );
    }

    const body = (!wallpaper && c.bg) ? (
        <div className="min-h-screen flex flex-col items-center justify-center px-5 py-8"
             style={{ background: c.bg }} data-testid="goal-fan-page">
            <div className="w-full max-w-sm">{inner}</div>
        </div>
    ) : (
        <BrandingShell
            wallpaperUrl={wallpaper}
            wallpaperZoom={brand?.wallpaper_zoom}
            variant="dark"
            contentClassName="max-w-sm mx-auto"
            data-testid="goal-fan-page"
        >
            {wash}
            <div className="relative">{inner}</div>
        </BrandingShell>
    );

    return <>{body}{opBar}{scoreWatch}</>;
};

export const GoalFanView = ({
    brand, state, lang, onLang, framed = false, opBar = null, cardId = null,
    // what the fan is looking at
    outcome, scratched, pinToken, tonightOpen, setupOpen,
    // the forms
    email, pin, consent, joining, error, lockSeconds,
    // the star pick (add-on, off by default — every prop below may be absent)
    starOffered = false, starPlayers = [], starStep = 1, starSelected = null,
    onStarSelect, onStarNext, onStarBack, onStarSkip,
    // the Good luck overview, shown once right after the star is saved
    goodLuck = null, onGoodLuckDone = null,
    // what the fan can do
    onTonightOpen, onTonightBack, onSetupStart, onEmailChange, onPinChange,
    screenName = '', onScreenNameChange = null,
    onConsentChange, onSetupSubmit, onSetupSave = null, onGoodLuck = null,
    onPinSubmit, onScratched, onCheckAgain, checking,
    needAccount = false, onAccountReady = null,
    confirmNeeded = false,
    onRegistered = null, joinUrl = '',
    // join this table
    isTableOwner = false, joinBusy = false, joinError = '', onJoinTable,
}) => {
    const { t: tr } = useTranslation();
    // Tonight's stats lives next to Tonight's contest and belongs to this
    // screen, so it is open on every state of it, like the score bar.
    const [statsOpen, setStatsOpen] = useState(false);
    // BROADCAST — the live players board. Opened from the black bar, and
    // straight away when the owner asked for the preview (?board=1).
    const [boardOpen, setBoardOpen] = useState(() => (
        typeof window !== 'undefined'
        && new URLSearchParams(window.location.search).get('board') === '1'));
    // Which of the board's own tabs the guest asked for from the main page.
    const [boardTab, setBoardTab] = useState('players');
    // Which slide-up panel the main page asked GoalScoreWatch for.
    const [scoreTab, setScoreTab] = useState(null);
    // Where the guest is inside the REGISTRATION flow: the picks now use the
    // same screens as the main page instead of one crowded page.
    const [setupScreen, setSetupScreen] = useState('winner');
    // True between the winner Lock in and the star Lock in of a player who has
    // just registered, so the star save ends on Good luck.
    const [fromSetup, setFromSetup] = useState(false);
    // ONE THING PER SCREEN. The main page only has buttons; every one of them
    // sends the guest to its own screen, and every screen but the main page
    // wears a Back button. 'main' is the page with the buttons on it.
    const [screen, setScreen] = useState('main');
    // Where the language screen should go back to: MY TAP'N WIN when it was
    // opened from there, the main page otherwise.
    const [langBack, setLangBack] = useState('main');
    const [pickTeam, setPickTeam] = useState(null);
    // SEND IT AGAIN — the result of the player's own retry of the email their
    // card promised.
    const [resending, setResending] = useState(false);
    const [resent, setResent] = useState(null);
    const [pickPlayer, setPickPlayer] = useState(null);
    const [locking, setLocking] = useState(false);
    // THE FIRST SCREEN OF ALL — which language is this phone in? Asked once per
    // phone and remembered; a guest who taps again on a later night goes
    // straight in. The admin preview never asks.
    const [langAsked, setLangAsked] = useState(() => {
        if (framed) return true;
        try { return !!localStorage.getItem(LANG_KEY); } catch (_e) { return true; }
    });
    // Set the moment the join form signs this phone in, so the Join button
    // does not wait for the next ten-second poll.
    const [patronReady, setPatronReady] = useState(false);
    // The preview switches language in the FRAME only, so every string is read
    // in the language this screen is showing, not the admin's own.
    const t = (key, opts) => tr(key, { lng: lang, ...(opts || {}) });

    // One lightweight count per fan-screen button. Fire-and-forget: it never
    // delays or blocks the button, and it never runs in the admin preview.
    const tap = (button) => {
        if (framed || !cardId) return;
        try {
            fetch(`${window.location.origin}/api/game-results/button-tap`, {
                method: 'POST', keepalive: true,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ card_id: cardId, button,
                    device_id: (typeof localStorage !== 'undefined'
                        ? localStorage.getItem('goalGameDeviceId') || '' : '') }),
            }).catch(() => { /* a count must never break a button */ });
        } catch (_e) { /* private mode */ }
    };

    // Operator wording, read on load. Order: the Goal Game override → the Gift
    // Generator setup's own text → the screen's built-in string.
    const txt = (key, giftKey) => {
        const own = (brand?.copy?.[lang] || {})[key];
        if (own) return own;
        const fromGift = giftKey ? (brand?.gift_copy?.[lang] || {})[giftKey] : '';
        if (fromGift) return fromGift;
        // Outside hockey the built-in default says "score", never "goal". The
        // operator's own wording still wins — this is only the fallback.
        if (brand?.league && brand.league !== 'nhl') {
            const forScore = t(`goalGame.score.${key}`, { defaultValue: '' });
            if (forScore) return forScore;
        }
        return t(`goalGame.${key}`);
    };
    const override = (key) => (brand?.copy?.[lang] || {})[key] || '';
    const prizeTitle = (prize) => (prize ? (lang === 'fr' ? prize.title_fr : prize.title_en) : '');
    const prizeWin = (prize) => (prize ? (lang === 'fr' ? prize.win_fr : prize.win_en) : '') || '';

    const c = palette(brand);
    const ty = typo(brand);
    // A prize that came from the Gift Generator wears THAT gift's design. The
    // server resolves it with the same resolver as the screen's own branding
    // and sends it beside the prize; no linked gift, no override.
    const prizeBrand = outcome?.prize_brand || state?.prize_brand || null;
    const pc = prizeBrand ? palette(prizeBrand) : c;
    const pty = prizeBrand ? typo(prizeBrand) : ty;
    const wonPrize = outcome?.prize || state?.prize || null;
    const nextVisit = wonPrize?.use_when === 'next_visit';
    // The table number staff set tonight (a demo fills it in by itself).
    const tableNumber = state?.table_number || null;
    const tableLabel = tableNumber?.label || '';
    const open = state?.event?.status === 'open';
    const left = state?.event?.seconds_left ?? 0;
    // The countdown a player reads: minutes and seconds, stopping at 0:00.
    const clock = (secs) => {
        const safe = Math.max(0, Math.floor(Number(secs) || 0));
        return `${Math.floor(safe / 60)}:${String(safe % 60).padStart(2, '0')}`;
    };
    const alreadyPlayed = !!state?.played && !scratched;
    const resendPrize = async () => {
        if (resending) return;
        setResending(true);
        try {
            const res = await postAsPlayer(`/api/goal-game/public/${cardId}/prize/resend`,
                { code: prizeVoucherCode, patron_device_id: deviceId() });
            const data = await res.json().catch(() => ({}));
            setResent({ sent: res.ok && !!data.sent, reason: data.reason || '' });
        } catch (_e) {
            setResent({ sent: false, reason: 'offline' });
        } finally { setResending(false); }
    };
    // THE EMAIL THE CARD PROMISES. Read off the card's own send log, so the
    // screen never says "we emailed it" when nothing left the building, and
    // offers Send it again when it did not.
    const prizeVoucher = outcome?.voucher || state?.voucher || null;
    const prizeVoucherCode = prizeVoucher?.claim_required ? null : (prizeVoucher?.code || null);
    const mailState = prizeVoucher?.email || null;
    const mineNotTable = !!mailState?.to_player || !!state?.at_home;
    const emailLine = (() => {
        if (!mailState) return null;        if (resent?.sent || mailState.sent) {
            return (
                <p className="text-[11px] opacity-70" data-testid="goal-prize-emailed">
                    {mineNotTable ? t('goalGame.emailedYou') : t('goalGame.nextVisitEmailed')}
                </p>
            );
        }
        return (
            <div className="mt-1" data-testid="goal-prize-email-failed">
                <p className="text-[11px] font-semibold" style={{ color: c.bad || '#fca5a5' }}>
                    {mailState.reason === 'email_not_confirmed'
                        ? t('goalGame.confirmFirst')
                        : t('goalGame.emailNotSent')}
                </p>
                <button type="button" onClick={resendPrize} disabled={resending}
                        data-testid="goal-prize-resend"
                        className="mt-1 min-h-[44px] w-full rounded-xl border border-white/30 px-4 text-sm font-bold">
                    {resending ? '…' : t('goalGame.sendItAgain')}
                </button>
                {resent && !resent.sent && (
                    <p className="mt-1 text-[11px] opacity-70" data-testid="goal-prize-resend-failed">
                        {t('goalGame.sendAgainFailed')}
                    </p>
                )}
            </div>
        );
    })();
    // MY OWN NEXT-VISIT GIFT from the table's goal. Every registered player at
    // the table gets their own separate roll; the table's immediate prize is
    // the table's. Absent = this player was not rolled for.
    const extra = state?.table_extra || null;
    const myExtra = !extra ? null : (
        <div className="mt-4 rounded-xl border px-4 py-3 text-sm"
             data-testid={extra.won ? 'goal-extra-win' : 'goal-extra-none'}
             style={{ borderColor: extra.won ? `${c.accent}66` : 'rgba(255,255,255,0.15)',
                      backgroundColor: 'rgba(255,255,255,0.06)' }}>
            {extra.won ? (
                <>
                    <p className="font-semibold" style={{ color: c.accent }}>
                        {t('goalGame.tableExtra.wonTitle')}
                    </p>
                    <p className="mt-1" data-testid="goal-extra-title">
                        {(lang === 'fr' && extra.title_fr) ? extra.title_fr : extra.title}
                    </p>
                    <p className="mt-1 text-xs opacity-80">{t('goalGame.tableExtra.wonBody')}</p>
                    {extra.code && (
                        <p className="mt-1 font-mono text-xs tracking-[0.18em] opacity-90"
                           data-testid="goal-extra-code">{extra.code}</p>
                    )}
                </>
            ) : (
                <p className="opacity-70" data-testid="goal-extra-none-line">
                    {t('goalGame.tableExtra.noneBody')}
                </p>
            )}
        </div>
    );
    // THE MAIN PAGE: the quiet page between rounds, the one with the buttons on
    // it. A live round, a result and the "already played" screen keep their own
    // layout untouched — the buttons are never drawn over the scratch card.
    const atMain = !!state?.table_set_up && screen === 'main'
        && state?.event?.status !== 'open' && !outcome && !alreadyPlayed;
    // A FUTURE-USE GIFT CARD says so with real dates, read off the voucher's
    // own existing redeemable_from / expires_at. No new stored field.
    const wonVoucher = outcome?.voucher || state?.voucher || null;
    const onDate = (iso) => {
        if (!iso) return '';
        try {
            const when = new Date(iso);
            if (Number.isNaN(when.getTime())) return '';
            return when.toLocaleDateString(lang === 'fr' ? 'fr-CA' : 'en-CA',
                                           { day: 'numeric', month: 'long',
                                             timeZone: 'America/Toronto' });
        } catch (_e) { return ''; }
    };
    const validLine = (() => {
        const from = onDate(wonVoucher?.redeemable_from);
        const until = onDate(wonVoucher?.expires_at);
        if (from && until) return t('goalGame.nextVisitValidDates', { from, until });
        if (until) return t('goalGame.nextVisitValidUntil', { until });
        return t('goalGame.nextVisitValidFrom');
    })();    const won = outcome?.result === 'win' || state?.result === 'win';
    // Unset → the celebration is on.
    const celebrate = brand?.design?.celebration_enabled !== false;
    // Frosted / Clear / Solid, in the sponsor's button colour. Unset = today.
    const primaryBtn = primarySkin(brand?.design?.button_style, c.button);
    const ghostBtn = ghostSkin(brand?.design?.button_style, c.button);

    // Tonight's contest and Tonight's stats are buttons on the main page's own
    // list now, like every other button; the black bar is gone.
    const statsPanel = statsOpen ? (
        <GoalStatsPanel cardId={cardId} t={t} title={txt('statsTitle')} brand={brand} c={c} ty={ty}
                        logoUrl={absUrl(brand?.logo_url)} framed={framed}
                        onClose={() => setStatsOpen(false)} />
    ) : null;

    // The live players board, inside this page — the fan never leaves.
    const boardPanel = boardOpen ? (
        <GoalLivePlayersBoard cardId={cardId} t={t} brand={brand} c={c} ty={ty} lang={lang}
                              logoUrl={absUrl(brand?.logo_url)} framed={framed}
                              startTab={boardTab}
                              onClose={() => setBoardOpen(false)} />
    ) : null;

    // The two score buttons live in the shell, so they are on EVERY state of
    // this screen; the panel closes itself the moment a round opens.
    // Score Watching is NHL data. The server says whether this promotion's
    // league has it; anything else hides the two buttons rather than showing
    // hockey scores under a football contest.
    const [patron, refreshPatron] = usePatronBadge(cardId, lang);
    // WELCOME TO TAPNPLAY — only where Patron Points is ON, only for a phone
    // that is not signed in, not remembered as the table, and not the admin
    // preview. 'done' means the person has been through it (or skipped it).
    const [flow, setFlow] = useState(null);
    const [tableSize, setTableSize] = useState(1);
    // A phone that has already chosen its language keeps it, with no question,
    // on every later game and every later visit.
    useEffect(() => {
        if (framed) return;
        try {
            const saved = localStorage.getItem(LANG_KEY);
            if (saved && saved !== lang) onLang?.(saved);
        } catch (_e) { /* private mode: the toggle still works */ }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // Entering the registration's pick step: the winning team comes first, and
    // the star straight after it when the promotion has no winner pick.
    useEffect(() => {
        if (starStep === 2) {
            setSetupScreen(state?.winner_pick?.enabled && state?.winner_pick?.mode !== 'card'
                ? 'winner' : 'starTeam');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [starStep]);
    const welcomeStep = flow || ((!patron.signed_in && !isTableOwner
        && !framed && !state?.joined) ? 'welcome' : null);
    // My points and Live players are buttons on the main page's own list now;
    // the bar that used to carry them is gone.
    const scoreWatch = (cardId && brand?.score_watch !== false) ? (
        <GoalScoreWatch cardId={cardId} lang={lang} t={t} brand={brand} c={c} ty={ty}
                        logoUrl={absUrl(brand?.logo_url)} goalLive={open}
                        primaryBtn={primaryBtn} ghostBtn={ghostBtn}
                        scoresBtn={scoreBarSkin(brand?.design, 'scores_button', ghostBtn, c)}
                        standingsBtn={scoreBarSkin(brand?.design, 'standings_button', ghostBtn, c)}
                        hideBar
                        requestTab={scoreTab}
                        onTabClosed={() => setScoreTab(null)}
                        onButtonTap={(which) => tap(which)}
                        framed={framed} raised={!!opBar} />
    ) : null;

    // The table's own pick, shown under every screen once it exists. The pick
    // is FINAL for the night, so the bar never reopens the list.
    const star = state?.star || null;
    // ── the guest flow's own pieces ──────────────────────────────────────
    const winnerBlock = state?.winner_pick || null;
    const winnerMine = (winnerBlock?.teams || []).find((x) => x.side === winnerBlock?.my_pick) || null;
    const starAvailable = !!state?.star_pick && starPlayers.length > 0;
    const starNow = star
        || (patron.pick ? { name: patron.pick.player_name } : null)
        || (pickPlayer ? { name: pickPlayer.name, number: pickPlayer.number } : null);
    // "Your star: Nick Suzuki #14" — the number only when we know it.
    const starLabel = starNow
        ? `${starNow.name}${starNow.number ? ` #${starNow.number}` : ''}` : '';
    // A PLAYER WHO IS NOT SIGNED IN SEES NO PICK BUTTONS, anywhere. The picks
    // belong to an account: they are saved against it, so offering them before
    // there is one is what sent people round in circles.
    // THE STAR HEADING — big, with the sport image for tonight's league beside
    // the word "star". No image uploaded yet: a plain star icon, never a player
    // photo, a team logo or anything invented.
    const sportImage = (brand?.sport_images || {})[
        String(state?.league || brand?.league || '').toLowerCase()] || '';
    const starHeading = (
        <div className="flex items-center justify-center gap-3 mb-2" data-testid="goal-star-heading">
            <span className="text-3xl sm:text-4xl font-black leading-none" style={ty.headline}>
                {t('goalGame.guest.starTeamTitle')}
            </span>
            {sportImage
                ? <img src={absUrl(sportImage)} alt="" className="h-12 w-12 object-contain"
                       data-testid="goal-star-sport-image" />
                : <Star size={34} style={{ color: c.accent }} data-testid="goal-star-sport-icon" />}
        </div>
    );

    const picksHidden = !framed && !patron.signed_in && !isTableOwner;
    // The word tonight's sport uses for a score — goal, basket, touchdown. The
    // operator's own wording wins, exactly as everywhere else on this screen.
    const eventWord = txt('eventWord');
    // Choose your star shows the SAME two teams as Choose your winner. With the
    // winner pick off, the teams are read off the roster itself.
    const duelTeams = (winnerBlock?.teams || []).length
        ? winnerBlock.teams
        : [...new Set((starPlayers || []).map((p) => p.team).filter(Boolean))]
            .map((ab, i) => ({ side: i === 0 ? 'home' : 'away', abbrev: ab, name: ab }));

    const toMain = () => { setScreen('main'); setPickTeam(null); setPickPlayer(null); };

    const chooseLang = (code) => {
        try { localStorage.setItem(LANG_KEY, code); } catch (_e) { /* private mode */ }
        onLang?.(code);
        setLangAsked(true);
        // Back to wherever the language screen was opened from: MY TAP'N WIN
        // when it was reached from there, the main page otherwise.
        setScreen(langBack);
        setLangBack('main');
    };

    /** LOCK IN MY WINNER. The save is the EXISTING one — the same endpoint and
     *  the same server rules: changeable until the game starts, final after.
     *  "Lock in" adds no second lock of its own. */
    const lockWinner = async (inSetup = false) => {
        if (!pickTeam) return;
        setLocking(true);
        try {
            // A BRAND-NEW PLAYER HAS NO ACCOUNT YET, and the pick is saved
            // against the account — so Lock in creates it first, with the
            // e-mail, PIN and consent already typed and no star. Without this
            // the save answered "sign in first" and the screen sat there.
            if (inSetup && !framed) {
                const ok = await onSetupSave?.();
                if (ok === false) return;        // message is already on screen
                refreshPatron?.();
            }
            if (!framed && cardId) {
                const res = await postAsPlayer(
                    `${window.location.origin}/api/goal-game/public/${cardId}/winner-pick`,
                    { side: pickTeam.side });
                const out = await res.json().catch(() => ({}));
                if (!res.ok) {
                    // THE REASON, IN PLAIN WORDS, every time.
                    const who = takenBy(out.detail);
                    toast.error(who
                        ? t('goalGame.saveErrors.phoneTakenNamed', { player: who })
                        : t(`goalGame.saveErrors.${saveErrorKey(res.status, out.detail)}`));
                    // The account was just made, so the registration form is
                    // gone: put the player back on the team choice to try
                    // again rather than on a screen that cannot save.
                    if (inSetup) setScreen('winner');
                    return;
                }
                onCheckAgain?.();
            }
            toast.success(t('goalGame.winnerPick.saved'));
            setPickTeam(null);
            // The account now exists, so the rest of the sequence runs on the
            // normal screens: Choose your star, then Good luck, then the main
            // page. Back from here goes to a calm page, never to a PIN box.
            if (inSetup) { setFromSetup(true); setSetupScreen('winner'); }
            setScreen(starAvailable ? 'starTeam' : 'main');
        } catch (_e) { toast.error(t('goalGame.saveErrors.offline')); }
        finally { setLocking(false); }
    };

    /** LOCK IN MY STAR. Again the existing save, with the existing rules: open
     *  any time except while a round's countdown is running. */
    const lockStar = async () => {
        if (!pickPlayer) return;
        setLocking(true);
        try {
            if (!framed && cardId) {
                const res = await postAsPlayer(
                    `${window.location.origin}/api/patron/public/${cardId}/star-pick`,
                    { player_id: pickPlayer.id, at_home: !!state?.at_home });
                if (!res.ok) {
                    const out = await res.json().catch(() => ({}));
                    toast.error(out.detail === 'round_running'
                        ? t('goalGame.guest.starRoundRunning')
                        : t(`goalGame.saveErrors.${saveErrorKey(res.status, out.detail)}`));
                    return;
                }
                onCheckAgain?.();
            }
            refreshPatron?.();
            // A player who has just registered sees Good luck, exactly as the
            // old one-shot save did; everyone else gets the short confirmation
            // and the main page.
            if (fromSetup) {
                setFromSetup(false);
                onGoodLuck?.(pickPlayer);
                toMain();
                return;
            }
            setScreen('starDone');
            window.setTimeout(toMain, 2400);
        } catch (_e) { toast.error(t('goalGame.saveErrors.offline')); }
        finally { setLocking(false); }
    };

    // Room at the bottom for the button bar — two rows of 44px when score
    // watching is on, one row otherwise — and for the operator bar under it,
    // so nothing on this screen can end up hidden behind either.
    const twoRows = (cardId && brand?.score_watch !== false);
    // Room at the bottom for the bar, which is one row taller when the patron
    // button is there, plus the operator bar under it. It is now added ONCE,
    // by the shell, under the small Tapelligence line at the foot of the page,
    // so nothing a player needs is hidden behind either bar.
    const barClear = (twoRows ? (opBar ? 'pb-80' : 'pb-32') : (opBar ? 'pb-64' : 'pb-20'));
    const pad = '';
    const barSpacer = null;
    const starFooter = (extra) => (
        <div className={pad}>
            {/* Where are you playing tonight? Draws nothing for a player with
                one badge, no badge, or a venue already locked for tonight. */}
            <GoalVenueChoice cardId={cardId} skin={{ accent: c.accent }}
                             deviceId={typeof localStorage !== 'undefined'
                                 ? localStorage.getItem('patron_device_id') || '' : ''}
                             onChosen={() => refreshPatron?.()} />
            <GoalSponsorLine cardId={cardId} atHome={Boolean(state?.at_home)}
                             deviceId={typeof localStorage !== 'undefined'
                                 ? localStorage.getItem('patron_device_id') || '' : ''} />
            {/* NO PICK BUTTONS HERE. The winner and the star are reached from
                the registration sequence and from the main page's own two
                buttons — never as a second, hollow copy under another card. */}
            {extra}
        </div>
    );
    // THE THREE BIG BUTTONS' OWN COLOURS. Fixed, soft gradients, the same in
    // every venue: the venue's branding no longer feeds them, so a bright brand
    // colour can never make them shout again.
    const groupColour = {
        live: ['#0F5132', '#1E7F4B'],
        nhl: ['#0E4A8A', '#2F7FD6'],
        mine: ['#C65A00', '#FF8A3D'],
    };
    // The section's own accent: the line under its title and its button icons.
    const groupAccent = { live: '#1E7F4B', nhl: '#2F7FD6', mine: '#FF8A3D' };
    // NHL NOW is hockey only: another sport never sees it.
    const isHockey = !brand?.league || brand.league === 'nhl';
    // MY TAP'N WIN — read once, whenever one of its screens is open.
    const hubOpen = ['mine', 'invite', 'wins', 'history', 'friends', 'reminders']
        .includes(screen);
    const { hub, loading: hubLoading, setHub } = useMyHub(framed ? '' : cardId, hubOpen);

    // ── THE MAIN PAGE: buttons, and nothing else. Each one opens its own
    //    screen and takes the guest off this page. Normal-sized buttons.
    // THE PICKS — the first thing under the logo and the table label, above
    // any notice this page carries. Biggest, boldest thing on the screen until
    // they are made; a calm confirmed line with a check afterwards.
    const mainPicks = (
        <div className="space-y-2.5" data-testid="goal-main-picks">
            {!picksHidden && (state?.winner_pick?.mode === 'card' ? (
                <CardPicks cardId={cardId} block={state.winner_pick} t={t} c={c}
                           deviceId={deviceId()}
                           onPicked={() => refreshPatron?.()} />
            ) : winnerBlock?.enabled ? (
                <MenuButton icon={Trophy} c={c} tone="hero" done={!!winnerMine}
                            testid="goal-menu-winner"
                            label={winnerMine
                                ? t('goalGame.guest.menuWinnerDone', {
                                    team: winnerMine.name || winnerMine.abbrev })
                                : t('goalGame.guest.menuWinner')}
                            hint={winnerMine
                                ? t('goalGame.guest.menuWinnerChange')
                                : [t('goalGame.guest.menuWinnerHint'),
                                   (patron.enabled && Number(winnerBlock?.points || 0) > 0)
                                       ? t('goalGame.winnerPick.worth', {
                                           points: Number(winnerBlock.points).toLocaleString() })
                                       : ''].filter(Boolean).join(' · ')}
                            onClick={() => { tap('winner_pick'); setScreen('winner'); }} />
            ) : null)}
            {!picksHidden && starAvailable && (
                <MenuButton icon={Star} c={c} tone="hero" done={!!starNow}
                            testid="goal-menu-star"
                            label={starNow
                                ? t('goalGame.guest.menuStarMine', { name: starLabel })
                                : t('goalGame.guest.menuStar')}
                            hint={starNow ? t('goalGame.guest.menuStarChange')
                                : (patron.enabled ? t('goalGame.guest.menuStarHint') : undefined)}
                            onClick={() => { tap('star_pick'); setScreen('starTeam'); }} />
            )}
            {/* Once both picks are in, a friendly line points at the rest, so
                nobody is ever left staring at a finished screen. */}
            {!!winnerMine && !!starNow && (
                <p className="rounded-xl border border-white/15 bg-black/25 px-4 py-3 text-center text-sm font-semibold"
                   data-testid="goal-all-set-line">
                    {patron.enabled ? t('goalGame.guest.allSet')
                        : t('goalGame.player.allSet')}
                </p>
            )}
        </div>
    );
    // ── THE MAIN PAGE'S FIVE BUTTONS, three colours. Everything that used to
    //    be its own line is still here: it moved inside LIVE, NHL NOW or MY
    //    TAP'N WIN, each of which is its own screen with a way back.
    const mainMenu = (
        <div className={pad} data-testid="goal-main-menu">
            {myExtra}
            <div className="space-y-2.5">
                <MenuButton icon={Radio} c={c} tone="group" colour={groupColour.live}
                            testid="goal-menu-live" label={t('goalGame.groups.live')}
                            hint={t('goalGame.groups.liveHint')}
                            onClick={() => { tap('players'); setScreen('live'); }} />
                {isHockey && (
                    <MenuButton icon={BarChart3} c={c} tone="group" colour={groupColour.nhl}
                                testid="goal-menu-nhl" label={t('goalGame.groups.nhl')}
                                hint={t('goalGame.groups.nhlHint')}
                                onClick={() => { tap('stats'); setScreen('nhl'); }} />
                )}
                <MenuButton icon={Users} c={c} tone="group" colour={groupColour.mine}
                            testid="goal-menu-mine" label={t('goalGame.groups.mine')}
                            hint={t('goalGame.groups.mineHint')}
                            onClick={() => setScreen('mine')} />
                <MenuButton icon={Info} c={c} testid="goal-menu-tonight"
                            label={txt('tonightButton')}
                            onClick={() => { tap('tonight'); onTonightOpen?.(); }} />
                <MenuButton icon={LogOut} c={c} testid="goal-menu-bye"
                            label={t('goalGame.guest.menuBye')}
                            onClick={() => setScreen('bye')} />
            </div>
            {/* Where are you playing tonight, and the sponsor's line — under the
                buttons, never above the picks. */}
            <GoalVenueChoice cardId={cardId} skin={{ accent: c.accent }}
                             deviceId={deviceId()}
                             onChosen={() => refreshPatron?.()} />
            <GoalSponsorLine cardId={cardId} atHome={Boolean(state?.at_home)}
                             deviceId={deviceId()} />
        </div>
    );
    const shellProps = { lang, onLang, brand, opBar, framed, tableLabel, barPad: barClear,
                         footer: atMain ? mainMenu : starFooter(null),
                         scoreWatch: <>{scoreWatch}{statsPanel}{boardPanel}</> };
    // The bar clearance belongs to the page wrapper / footer, never INSIDE a
    // card, or every card ends with a tall empty strip under its last button.
    const panel = 'rounded-2xl border p-6 text-center';
    const panelCss = panelStyle(c);
    const field = 'w-full rounded-xl bg-white/10 border border-white/15 px-4 py-3 text-base outline-none';

    // ── THE VERY FIRST SCREEN: which language? Asked once per phone, before
    //    anything else, and never again on that phone.
    if (!langAsked) {
        return (
            <Shell {...shellProps} footer={barSpacer}>
                <div className={pad}>
                    <LanguageGate onPick={chooseLang} current={lang} c={c} ty={ty} t={t} />
                </div>
            </Shell>
        );
    }

    // ── CHANGE MY LANGUAGE — the same choice, from the main page, one tap.
    if (screen === 'lang') {
        return (
            <Shell {...shellProps} footer={barSpacer}>
                <div className={pad}>
                    <BackBar onBack={() => { setScreen(langBack); setLangBack('main'); }}
                             label={t('goalGame.back')} ty={ty} />
                    <LanguageGate onPick={chooseLang} current={lang} c={c} ty={ty} t={t} />
                </div>
            </Shell>
        );
    }


    // ── first load
    if (!state) {
        return (
            <Shell {...shellProps} footer={barSpacer}>
                <div className={`text-center ${pad}`} data-testid="goal-loading">
                    <Loader2 className="animate-spin mx-auto opacity-40" size={22} />
                </div>
            </Shell>
        );
    }

    // ── TONIGHT'S GAME IS FULL — the safety valve. Only a NEW player ever
    //    sees this; anybody already playing keeps their screen exactly as it
    //    was. It sits above every other screen so nothing of the game shows
    //    through behind it.
    if (state.capacity_full) {
        return (
            <Shell {...shellProps} footer={barSpacer}>
                <div className={pad}>
                    <div className={panel} style={panelCss} data-testid="goal-capacity-full">
                        <p className="text-2xl font-bold mb-2" style={ty.headline}
                           data-testid="goal-capacity-full-title">{txt('fullTitle')}</p>
                        <p className="text-base opacity-80" style={ty.body}
                           data-testid="goal-capacity-full-body">{txt('fullBody')}</p>
                    </div>
                </div>
            </Shell>
        );
    }

    // ── THE GUEST'S OWN SCREENS. One thing each, Back always top left. They
    //    sit above the game's states so a guest who walked into Choose your
    //    star is not yanked back by the next ten-second poll.
    if (screen !== 'main') {
        const body = (() => {
            const groupList = (items, accent = '') => (
                <div className="space-y-2.5" data-testid="goal-group-list">
                    {items.filter((item) => item && !item.hidden).map((item) => (
                        <MenuButton key={item.testid} icon={item.icon} c={c}
                                    accent={accent}
                                    testid={item.testid} label={item.label}
                                    onClick={item.onClick} />
                    ))}
                </div>
            );
            switch (screen) {
                // ── LIVE: who is playing, the leaderboard and the chat.
                case 'live':
                    return (
                        <>
                            <BackBar onBack={toMain} label={t('goalGame.back')}
                                     title={t('goalGame.groups.live')} ty={ty}
                                     accent={groupAccent.live} />
                            {groupList([
                                { testid: 'goal-live-players', icon: Users,
                                  label: t('goalGame.boardButton'),
                                  onClick: () => { tap('players'); setBoardTab('players'); setBoardOpen(true); } },
                                { testid: 'goal-live-leaderboard', icon: BarChart3,
                                  label: t('goalGame.navLeaderboard'),
                                  onClick: () => { tap('players'); setBoardTab('leaderboard'); setBoardOpen(true); } },
                                { testid: 'goal-live-chat', icon: MessageCircle,
                                  label: t('goalGame.navChat'),
                                  // Chat off at this venue: no button at all.
                                  hidden: state?.live_chat === false,
                                  onClick: () => { tap('players'); setBoardTab('chat'); setBoardOpen(true); } },
                            ], groupAccent.live)}
                        </>
                    );
                // ── NHL NOW: tonight's stats, the live scores, the standings.
                case 'nhl':
                    return (
                        <>
                            <BackBar onBack={toMain} label={t('goalGame.back')}
                                     title={t('goalGame.groups.nhl')} ty={ty}
                                     accent={groupAccent.nhl} />
                            {groupList([
                                { testid: 'goal-nhl-stats', icon: BarChart3,
                                  label: txt('statsButton'),
                                  onClick: () => { tap('stats'); setStatsOpen(true); } },
                                { testid: 'goal-nhl-scores', icon: Radio,
                                  label: t('goalGame.liveScores'),
                                  onClick: () => { tap('scores'); setScoreTab('scores'); } },
                                { testid: 'goal-nhl-standings', icon: ListOrdered,
                                  label: t('goalGame.standings'),
                                  onClick: () => { tap('standings'); setScoreTab('standings'); } },
                            ], groupAccent.nhl)}
                        </>
                    );
                // ── MY TAP'N WIN: the player's own corner.
                case 'mine':
                    return (
                        <>
                            <BackBar onBack={toMain} label={t('goalGame.back')}
                                     title={t('goalGame.groups.mine')} ty={ty}
                                     accent={groupAccent.mine} />
                            {groupList([
                                { testid: 'goal-mine-invite', icon: Users,
                                  label: t('goalGame.hub.inviteTitle'),
                                  onClick: () => setScreen('invite') },
                                { testid: 'goal-mine-wins', icon: Trophy,
                                  label: t('goalGame.hub.winsTitle'),
                                  onClick: () => setScreen('wins') },
                                { testid: 'goal-mine-history', icon: RotateCw,
                                  label: t('goalGame.hub.historyTitle'),
                                  onClick: () => setScreen('history') },
                                { testid: 'goal-mine-friends', icon: Users,
                                  label: t('goalGame.hub.friendsTitle'),
                                  onClick: () => setScreen('friends') },
                                { testid: 'goal-mine-language', icon: Languages,
                                  label: t('goalGame.guest.menuLanguage'),
                                  onClick: () => { setLangBack('mine'); setScreen('lang'); } },
                                { testid: 'goal-mine-reminders', icon: Bell,
                                  label: t('goalGame.reminders.title'),
                                  onClick: () => setScreen('reminders') },
                            ], groupAccent.mine)}
                        </>
                    );
                case 'invite':
                    return (
                        <>
                            <BackBar onBack={() => setScreen('mine')} label={t('goalGame.back')} ty={ty} />
                            <GoalInviteScreen hub={hub} loading={hubLoading} t={t} c={c} ty={ty}
                                              primaryBtn={primaryBtn} ghostBtn={ghostBtn} />
                        </>
                    );
                case 'wins':
                    return (
                        <>
                            <BackBar onBack={() => setScreen('mine')} label={t('goalGame.back')} ty={ty} />
                            <GoalWinsScreen hub={hub} loading={hubLoading} t={t} c={c} ty={ty} lang={lang} />
                        </>
                    );
                case 'history':
                    return (
                        <>
                            <BackBar onBack={() => setScreen('mine')} label={t('goalGame.back')} ty={ty} />
                            <GoalHistoryScreen hub={hub} loading={hubLoading} t={t} c={c} ty={ty} />
                        </>
                    );
                case 'friends':
                    return (
                        <>
                            <BackBar onBack={() => setScreen('mine')} label={t('goalGame.back')} ty={ty} />
                            <GoalFriendsScreen hub={hub} loading={hubLoading} t={t} c={c} ty={ty} />
                        </>
                    );
                case 'winner':
                    return (
                        <>
                            <BackBar onBack={toMain} label={t('goalGame.back')} ty={ty} />
                            <TeamDuel teams={winnerBlock?.teams || []} mine={winnerBlock?.my_pick}
                                      t={t} c={c} ty={ty}
                                      title={t('goalGame.guest.winnerTitle')}
                                      body={winnerBlock?.locked
                                          ? t('goalGame.winnerPick.locked')
                                          : (patron.enabled
                                              ? t('goalGame.winnerPick.worth', {
                                                  points: Number(winnerBlock?.points || 0).toLocaleString() })
                                              : t('goalGame.guest.menuWinnerHint'))}
                                      homeLabel={t('goalGame.guest.home')}
                                      awayLabel={t('goalGame.guest.away')}
                                      onPick={(team) => { setPickTeam(team); setScreen('winnerLock'); }} />
                        </>
                    );
                case 'winnerLock':
                    return (
                        <>
                            <BackBar onBack={() => setScreen('winner')} label={t('goalGame.back')} ty={ty} />
                            <LockConfirm kicker={t('goalGame.guest.winnerLockKicker')}
                                         name={pickTeam?.name || pickTeam?.abbrev || ''}
                                         sub={pickTeam?.rank || ''}
                                         note={t('goalGame.guest.winnerLockNote')}
                                         lockLabel={t('goalGame.guest.lockIn')}
                                         onLock={() => lockWinner(false)} busy={locking}
                                         primaryBtn={primaryBtn} c={c} ty={ty}
                                         accentColour={pickTeam ? undefined : c.button} />
                        </>
                    );
                case 'starTeam':
                    return (
                        <>
                            <BackBar onBack={toMain} label={t('goalGame.back')} ty={ty} />
                            <TeamDuel teams={duelTeams} t={t} c={c} ty={ty} star
                                      heading={starHeading}
                                      title={t('goalGame.player.starTeamLine')}
                                      homeLabel={t('goalGame.guest.home')}
                                      awayLabel={t('goalGame.guest.away')}
                                      onPick={(team) => { setPickTeam(team); setScreen('starRoster'); }} />
                        </>
                    );
                case 'starRoster':
                    return (
                        <>
                            <BackBar onBack={() => setScreen('starTeam')} label={t('goalGame.back')} ty={ty} />
                            <RosterList
                                players={(starPlayers || []).filter(
                                    (p) => !pickTeam?.abbrev || p.team === pickTeam.abbrev)}
                                t={t} c={c} ty={ty} field={field}
                                title={t('goalGame.guest.rosterTitle', {
                                    team: pickTeam?.name || pickTeam?.abbrev || '' })}
                                searchLabel={txt('starSearch')}
                                onPick={(p) => { setPickPlayer(p); setScreen('starLock'); }} />
                        </>
                    );
                case 'starLock':
                    return (
                        <>
                            <BackBar onBack={() => setScreen('starRoster')} label={t('goalGame.back')} ty={ty} />
                            <LockConfirm icon="star"
                                         kicker={t('goalGame.guest.starLockKicker')}
                                         name={pickPlayer?.name || ''}
                                         sub={[pickPlayer?.number ? `#${pickPlayer.number}` : '',
                                               pickPlayer?.position || ''].filter(Boolean).join(' · ')}
                                         note={t('goalGame.guest.starLockNote', { event: eventWord })}
                                         lockLabel={t('goalGame.guest.lockItIn')}
                                         onLock={lockStar} busy={locking}
                                         primaryBtn={primaryBtn} c={c} ty={ty} />
                        </>
                    );
                case 'starDone':
                    return (
                        <>
                            <BackBar onBack={toMain} label={t('goalGame.back')} ty={ty} />
                            <div className={panel} style={panelCss} data-testid="goal-star-locked">
                                <Star size={34} className="mx-auto mb-3" style={{ color: c.accent }} />
                                <p className="text-2xl font-bold" style={ty.headline}
                                   data-testid="goal-star-locked-title">
                                    {t('goalGame.guest.starLocked')}
                                </p>
                                <p className="mt-1 text-base font-semibold"
                                   data-testid="goal-star-locked-name">{pickPlayer?.name || starNow?.name || ''}</p>
                                <p className="mt-3 text-sm opacity-70">{t('goalGame.guest.starLockedBody')}</p>
                            </div>
                        </>
                    );
                case 'bye':
                    return (
                        <>
                            <BackBar onBack={toMain} label={t('goalGame.back')} ty={ty} />
                            <ByeScreen teamName={winnerMine?.name || winnerMine?.abbrev || ''}
                                       starName={starNow?.name || ''}
                                       reminder={t('goalGame.guest.byeReminder', { event: eventWord })}
                                       t={t} c={c} ty={ty} primaryBtn={primaryBtn} onBack={toMain} />
                        </>
                    );
                case 'reminders':
                    return (
                        <>
                            <BackBar onBack={() => setScreen(atMain ? 'main' : 'mine')}
                                     label={t('goalGame.back')} ty={ty} />
                            <div className="space-y-3">
                                <GoalReminderScreen cardId={cardId} hub={hub} loading={hubLoading}
                                                    t={t} c={c} ty={ty}
                                                    primaryBtn={primaryBtn} ghostBtn={ghostBtn}
                                                    onSaved={(next) => setHub((prev) => (
                                                        prev ? { ...prev, reminder: next } : prev))} />
                                <GoalEntryBlock cardId={cardId} lang={lang} t={t} badge={patron}
                                                onChanged={refreshPatron} c={c} ty={ty}
                                                primaryBtn={primaryBtn} ghostBtn={ghostBtn}
                                                pointsColour={brand?.design?.scores_button || '#34d399'} />
                            </div>
                        </>
                    );
                default:
                    return null;
            }
        })();
        if (body) {
            return (
                <Shell {...shellProps} footer={barSpacer}>
                    <div className={pad} data-testid={`goal-screen-${screen}`}>{body}</div>
                </Shell>
            );
        }
    }


    // ── GOOD LUCK — straight after the star is saved, once, then the main
    //    screen. It sits above every other screen so Save always answers.
    //    The one exception is the welcome flow's own steps: the registration
    //    that starts with the Patron Points account finishes with the table
    //    size first, and the Good luck screen comes straight after it.
    if (goodLuck && !['welcome', 'register', 'size'].includes(welcomeStep)) {
        return (
            <Shell {...shellProps} footer={barSpacer}>
                <div className={pad}>
                <GoalGoodLuck star={goodLuck.star} winner={state?.winner_pick}
                              cardId={cardId} lang={lang} t={t} txt={txt} c={c} ty={ty}
                              deviceId={typeof localStorage !== 'undefined'
                                  ? localStorage.getItem('patron_device_id') || '' : ''}
                              primaryBtn={primaryBtn} panel={panel} panelCss={panelCss}
                              pointsOn={!!patron.enabled}
                              onRefresh={() => refreshPatron?.()}
                              onDone={onGoodLuckDone} />
                </div>
            </Shell>
        );
    }

    // ── Tonight's game, reachable from every screen
    if (tonightOpen) {        return (
            <Shell {...shellProps} footer={starFooter(null)}>
                <div className={panel} style={panelCss} data-testid="goal-tonight-panel">
                    <p className="text-lg font-bold mb-2" style={ty.headline} data-testid="goal-tonight-title">{txt('tonightTitle')}</p>
                    <p className="text-sm opacity-70" data-testid="goal-tonight-body">{txt('tonightBody')}</p>
                    <button onClick={onTonightBack} data-testid="goal-tonight-back" style={ghostBtn}
                            className="mt-6 inline-flex items-center gap-2 rounded-xl border border-white/25 px-4 py-2 text-sm font-semibold">
                        <ArrowLeft size={14} />{t('goalGame.back')}
                    </button>
                </div>
            </Shell>
        );
    }

    // ── WHERE THIS BUSINESS LETS PEOPLE PLAY. A place that is switched off is
    // a friendly message and nothing else: no registration, no picks, no play.
    if (state?.place_blocked && !framed) {
        return (
            <Shell {...shellProps} footer={barSpacer}>
                <div className={pad}>
                    <div className="rounded-2xl border p-6 text-center" style={panelCss}
                         data-testid="goal-place-blocked">
                        <MapPin size={28} className="mx-auto mb-3" style={{ color: c.accent }} />
                        <p className="text-lg font-bold" style={ty.headline}
                           data-testid="goal-place-blocked-text">
                            {t(`goalGame.placeOff.${state.place_blocked}`,
                               { venue: brand?.name || t('goalGame.placeOff.venue') })}
                        </p>
                    </div>
                </div>
            </Shell>
        );
    }

    // ── SIGN IN OR REGISTER — the only thing between a person and the
    // game: an email, a screen name and a four-digit code. Nothing about any
    // points programme is on the player's path.
    if ((welcomeStep === 'welcome' || welcomeStep === 'register') && !needAccount) {
        return (
            <Shell {...shellProps} footer={barSpacer}>
                <div className={pad}>
                    <div className="rounded-2xl border p-5" style={panelCss}
                         data-testid="goal-welcome-screen">
                        <p className="text-base font-semibold mb-3" data-testid="goal-welcome-game">
                            {txt('tonightTitle')}
                        </p>
                        <GoalPlayerAccount cardId={cardId} t={t} c={c} ty={ty} lang={lang}
                                           field={field} primaryBtn={primaryBtn}
                                           deviceId={deviceId()} atHome={!!state?.at_home}
                                           onDone={(d, creds) => {
                                               onRegistered?.(d, creds);
                                               refreshPatron?.();
                                               setFlow('size');
                                           }} />
                    </div>
                </div>
            </Shell>
        );
    }

    // ── ALREADY A PLAYER — email and code only.
    if (welcomeStep === 'signin') {
        return (
            <Shell {...shellProps} footer={barSpacer}>
                <div className={pad}>
                    <div className="rounded-2xl border p-5" style={panelCss}
                         data-testid="goal-signin-screen">
                        <BackBar onBack={() => setFlow(null)} label={t('goalGame.back')} ty={ty} />
                        <GoalPlayerAccount cardId={cardId} t={t} c={c} ty={ty} lang={lang}
                                           field={field} primaryBtn={primaryBtn}
                                           deviceId={deviceId()} startBack
                                           atHome={!!state?.at_home}
                                           onDone={(d, creds) => {
                                               onRegistered?.(d, creds);
                                               refreshPatron?.();
                                               setFlow(null);
                                           }} />
                    </div>
                </div>
            </Shell>
        );
    }

    // ── how many people are at your table?
    if (welcomeStep === 'size') {
        return (
            <Shell {...shellProps} footer={barSpacer}>
                <div className={pad}>
                    <div className="rounded-2xl border p-5 text-center" style={panelCss}
                         data-testid="goal-table-size-screen">
                        <p className="text-lg font-bold" style={ty.headline}
                           data-testid="goal-table-size-title">
                            {t('goalGame.player.headcount')}
                        </p>
                        <p className="mt-2 text-sm opacity-80" data-testid="goal-table-size-body">
                            {t('goalGame.player.headcountBody')}
                        </p>
                        <div className="mt-5 flex items-center justify-center gap-5">
                            <button type="button" onClick={() => setTableSize(Math.max(1, tableSize - 1))}
                                    data-testid="goal-table-size-minus" style={ghostBtn}
                                    className="h-14 w-14 rounded-full border border-white/25 text-2xl font-bold">
                                −
                            </button>
                            <span className="text-4xl font-bold tabular-nums w-16"
                                  data-testid="goal-table-size-value">{tableSize}</span>
                            <button type="button" onClick={() => setTableSize(Math.min(10, tableSize + 1))}
                                    data-testid="goal-table-size-plus" style={ghostBtn}
                                    className="h-14 w-14 rounded-full border border-white/25 text-2xl font-bold">
                                +
                            </button>
                        </div>
                        {tableSize > 1 && (
                            <div className="mt-5 rounded-xl border border-white/15 bg-black/25 p-4"
                                 data-testid="goal-table-size-invite">
                                <p className="text-sm">{t('tapnplay.inviteOthers')}</p>
                                {joinUrl && (
                                    <div className="mt-3 flex justify-center">
                                        <QRCodeSVG value={joinUrl} size={132} includeMargin
                                                   data-testid="goal-table-size-qr" />
                                    </div>
                                )}
                            </div>
                        )}
                        <button type="button" onClick={() => setFlow('done')} style={primaryBtn}
                                data-testid="goal-table-size-done"
                                className="mt-5 mb-4 w-full rounded-xl font-bold py-4 shadow-lg min-h-[56px]">
                            {t('tapnplay.sizeDone')}
                        </button>
                    </div>
                </div>
            </Shell>
        );
    }

    // ── A brand-new account has to tap the link in its confirmation email.
    if (confirmNeeded) {
        return (
            <Shell {...shellProps} footer={barSpacer}>
                <div className="rounded-2xl border p-6 text-center" style={panelCss}
                     data-testid="goal-confirm-email">
                    <p className="text-lg font-bold mb-2" style={ty.headline}>
                        {t('goalGame.confirmEmailTitle')}
                    </p>
                    <p className="text-sm opacity-80">{t('goalGame.confirmEmailBody')}</p>
                </div>
            </Shell>
        );
    }

    // ── Patron Points is ON and this phone tapped Scratch without an account
    // → the SAME account form, then straight back to the scratch.
    if (needAccount) {
        return (
            <Shell {...shellProps} footer={barSpacer}>
                <div className={pad}>
                    <div className="rounded-2xl border p-5" style={panelCss}
                         data-testid="goal-scratch-signin">
                        <p className="text-lg font-bold mb-1 text-center" style={ty.headline}
                           data-testid="goal-scratch-signin-title">
                            {t('goalGame.scratchSignInTitle')}
                        </p>
                        <p className="text-sm opacity-70 text-center mb-3"
                           data-testid="goal-scratch-signin-body">
                            {t('goalGame.scratchSignInBody')}
                        </p>
                        <GoalPlayerAccount cardId={cardId} t={t} c={c} ty={ty} lang={lang}
                                           field={field} primaryBtn={primaryBtn}
                                           deviceId={deviceId()}
                                           onDone={(d) => { refreshPatron?.(); if (d?.signed_in) onAccountReady?.(); }} />
                    </div>
                </div>
            </Shell>
        );
    }

    // ── the table is already set up, this phone is not the one that set it up
    // and has not sat down yet → "This table is already set to play".
    if (state.table_set_up && !isTableOwner && !state.joined) {
        const ready = patron.signed_in || patronReady;
        const full = !!state.table_full;
        return (
            <Shell {...shellProps} footer={barSpacer}>
                <div className={pad}>
                <div className="rounded-2xl border p-5" style={panelCss}
                     data-testid="goal-join-screen">
                    <p className="text-lg font-bold mb-1 text-center" style={ty.headline}
                       data-testid="goal-join-title">
                        {txt('joinTitle').replace('{{venue}}',
                            brand?.name || state?.venue_name || '').trim()}
                    </p>
                    <p className="text-sm opacity-70 text-center" data-testid="goal-join-body">
                        {full ? txt('joinFull').replace('{max}', state.max_players)
                              : (patron.enabled ? txt('joinBody')
                                  : t('goalGame.player.joinBody'))}
                    </p>
                    {!full && (
                        <>
                            {/* ONE BUTTON AT A TIME. Until this phone has a
                                player on it, the only button is the one that
                                makes them a player; once it does, the only
                                button is the one that joins this table. How
                                joining works is unchanged. */}
                            {!ready && (
                                <GoalPlayerAccount cardId={cardId} t={t} c={c} ty={ty} lang={lang}
                                                   field={field} primaryBtn={primaryBtn}
                                                   deviceId={deviceId()}
                                                   onDone={(d, creds) => {
                                                       setPatronReady(!!d?.signed_in);
                                                       onRegistered?.(d, creds);
                                                       refreshPatron?.();
                                                   }} />
                            )}
                            {ready && (
                            <button onClick={onJoinTable} disabled={!ready || joinBusy}
                                    data-testid="goal-join-button" style={primaryBtn}
                                    className="w-full min-h-[44px] rounded-xl font-bold py-3 mt-4 shadow-lg disabled:opacity-50">
                                {joinBusy ? <Loader2 className="animate-spin mx-auto" size={20} />
                                          : txt('joinButton')}
                            </button>
                            )}
                            {joinError && (
                                <p className="text-sm mt-2 text-center text-red-300"
                                   data-testid="goal-join-error">
                                    {joinError === 'table_full'
                                        ? txt('joinFull').replace('{max}', state.max_players)
                                        : joinError === 'sign_in_first' ? txt('joinSignInFirst')
                                        : txt('joinFailed')}
                                </p>
                            )}
                        </>
                    )}
                </div>
                </div>
            </Shell>
        );
    }

    // ── staff have not named this table yet → "Waiting for staff"
    //    Nothing can be registered until they do; a demo never lands here.
    // An at-home player (game-day link or venue join link) never waits for
    // staff; an in-bar table card keeps the staff table step untouched.
    if (state.table_number?.required && !state.at_home) {
        return (
            <Shell {...shellProps} footer={barSpacer}>
                {/* A player at home on this link can still be placed under one
                    of their badge venues while staff name the table. */}
                <GoalVenueChoice cardId={cardId} skin={{ accent: c.accent }}
                                 deviceId={typeof localStorage !== 'undefined'
                                     ? localStorage.getItem('patron_device_id') || '' : ''}
                                 onChosen={() => refreshPatron?.()} />
                <div className={panel} style={panelCss} data-testid="goal-waiting-staff-screen">
                    <p className="text-lg font-bold mb-1" style={ty.headline}
                       data-testid="goal-waiting-staff-title">{t('goalGame.waitingStaffTitle')}</p>
                    <p className="text-sm opacity-60 mb-5"
                       data-testid="goal-waiting-staff-body">{t('goalGame.waitingStaffBody')}</p>
                    <GoalStaffTable cardId={cardId} label="" onDone={onCheckAgain}
                                    c={c} field={field} primaryBtn={primaryBtn}
                                    ghostBtn={ghostBtn} />
                </div>
            </Shell>
        );
    }

    // ── the table has not been set up tonight → Tonight's game + Set up my table
    if (!state.table_set_up) {
        return (
            <Shell {...shellProps} footer={barSpacer}>
                {/* The entry block — guest, log in, or register for the venue's
                    own programme — above "Tonight's game" and nowhere else. */}
                <GoalEntryBlock cardId={cardId} lang={lang} t={t} badge={patron}
                                onChanged={refreshPatron} c={c} ty={ty}
                                primaryBtn={primaryBtn} ghostBtn={ghostBtn}
                                pointsColour={brand?.design?.scores_button || '#34d399'} />
                <div className={panel} style={panelCss} data-testid="goal-table-setup-screen">
                    <p className="text-lg font-bold mb-1" style={ty.headline} data-testid="goal-tonight-title">{state.at_home ? t('goalGame.homeStartTitle') : txt('tonightTitle')}</p>
                    <p className="text-sm opacity-60 mb-5" data-testid="goal-tonight-body">{state.at_home ? t('goalGame.homeStartBody') : txt('tonightBody')}</p>
                    {!setupOpen ? (
                        <button onClick={onSetupStart}
                                data-testid="goal-setup-start"
                                style={primaryBtn}
                                className="w-full rounded-xl font-bold py-3 shadow-lg">
                            {state.at_home ? t('goalGame.homeStartButton') : txt('setupButton')}
                        </button>
                    ) : (starOffered && starStep === 2) ? (
                        /* STEP 2 — THE PICKS, one thing per screen, the same
                           screens the main page uses. Nothing is stored until
                           the star is locked in: that tap submits the SAME
                           single registration call as before, with the same
                           e-mail, PIN, consent and star. */
                        <div className="text-left" data-testid="goal-setup-picks">
                            {setupScreen === 'winner' && (
                                <>
                                    <BackBar onBack={onStarBack} label={t('goalGame.back')} ty={ty} />
                                    <TeamDuel teams={winnerBlock?.teams || []} mine={winnerBlock?.my_pick}
                                              t={t} c={c} ty={ty}
                                              title={t('goalGame.guest.winnerTitle')}
                                              body={patron.enabled
                                                  ? t('goalGame.winnerPick.worth', {
                                                      points: Number(winnerBlock?.points || 0).toLocaleString() })
                                                  : t('goalGame.guest.menuWinnerHint')}
                                              homeLabel={t('goalGame.guest.home')}
                                              awayLabel={t('goalGame.guest.away')}
                                              onPick={(team) => { setPickTeam(team); setSetupScreen('winnerLock'); }} />
                                </>
                            )}
                            {setupScreen === 'winnerLock' && (
                                <>
                                    <BackBar onBack={() => setSetupScreen('winner')} label={t('goalGame.back')} ty={ty} />
                                    <LockConfirm kicker={t('goalGame.guest.winnerLockKicker')}
                                                 name={pickTeam?.name || pickTeam?.abbrev || ''}
                                                 sub={pickTeam?.rank || ''}
                                                 note={t('goalGame.guest.winnerLockNote')}
                                                 lockLabel={t('goalGame.guest.lockIn')}
                                                 onLock={() => lockWinner(true)} busy={locking}
                                                 primaryBtn={primaryBtn} c={c} ty={ty} />
                                </>
                            )}
                            {setupScreen === 'starTeam' && (
                                <>
                                    <BackBar onBack={() => setSetupScreen(winnerBlock?.enabled ? 'winner' : 'start')}
                                             label={t('goalGame.back')} ty={ty} />
                                    <TeamDuel teams={duelTeams} t={t} c={c} ty={ty} star
                                              heading={starHeading}
                                              title={t('goalGame.player.starTeamLine')}
                                              homeLabel={t('goalGame.guest.home')}
                                              awayLabel={t('goalGame.guest.away')}
                                              onPick={(team) => { setPickTeam(team); setSetupScreen('starRoster'); }} />
                                </>
                            )}
                            {setupScreen === 'starRoster' && (
                                <>
                                    <BackBar onBack={() => setSetupScreen('starTeam')} label={t('goalGame.back')} ty={ty} />
                                    <RosterList
                                        players={(starPlayers || []).filter(
                                            (p) => !pickTeam?.abbrev || p.team === pickTeam.abbrev)}
                                        t={t} c={c} ty={ty} field={field}
                                        title={t('goalGame.guest.rosterTitle', {
                                            team: pickTeam?.name || pickTeam?.abbrev || '' })}
                                        searchLabel={txt('starSearch')}
                                        onPick={(p) => {
                                            tap('players');
                                            setPickPlayer(p);
                                            onStarSelect?.(p);      // the ONE save reads this
                                            setSetupScreen('starLock');
                                        }} />
                                </>
                            )}
                            {setupScreen === 'starLock' && (
                                <form onSubmit={(e) => {
                                    e.preventDefault();
                                    // THE STAR MUST ALWAYS SAVE. Only a player
                                    // who typed the table form here has an
                                    // e-mail and code to set the table up with;
                                    // anybody else is already a player, so the
                                    // star goes through the ordinary save.
                                    if (email && /^\d{4}$/.test(pin || '')) onSetupSubmit(e);
                                    else lockStar();
                                }} data-testid="goal-setup-star-lock">
                                    <BackBar onBack={() => setSetupScreen('starRoster')} label={t('goalGame.back')} ty={ty} />
                                    <div className="rounded-2xl border p-6 text-center"
                                         style={{ backgroundColor: 'rgba(255,255,255,0.07)',
                                                  borderColor: `${c.accent}33` }}>
                                        <Star size={30} className="mx-auto mb-3" style={{ color: c.accent }} />
                                        <p className="text-sm font-semibold uppercase tracking-wider opacity-70"
                                           data-testid="goal-lock-kicker">
                                            {t('goalGame.guest.starLockKicker')}
                                        </p>
                                        <p className="mt-1 text-3xl font-black leading-tight" style={ty.headline}
                                           data-testid="goal-lock-name">{pickPlayer?.name || ''}</p>
                                        <p className="mt-1 text-sm opacity-70">
                                            {[pickPlayer?.number ? `#${pickPlayer.number}` : '',
                                              pickPlayer?.position || ''].filter(Boolean).join(' · ')}
                                        </p>
                                        <p className="mt-3 text-xs opacity-60" data-testid="goal-lock-note">
                                            {t('goalGame.guest.starLockNote', { event: eventWord })}
                                        </p>
                                        <button type="submit" disabled={joining} style={primaryBtn}
                                                data-testid="goal-lock-button"
                                                className="mt-6 w-full min-h-[56px] rounded-xl py-4 text-lg font-bold shadow-lg disabled:opacity-60">
                                            {joining ? <Loader2 className="mx-auto animate-spin" size={20} />
                                                : t('goalGame.guest.lockItIn')}
                                        </button>
                                        <button type="button" onClick={onStarSkip} disabled={joining}
                                                data-testid="goal-star-skip" style={ghostBtn}
                                                className="mt-2 w-full rounded-xl border border-white/25 py-2.5 text-sm font-semibold">
                                            {txt('starSkip')}
                                        </button>
                                    </div>
                                </form>
                            )}
                            {/* Close and come back later, on the pick screens too. */}
                            <button type="button" onClick={() => setScreen('bye')}
                                    data-testid="goal-setup-bye"
                                    className="mt-4 w-full min-h-[48px] rounded-xl border border-white/20 bg-white/5 px-4 py-3 text-sm font-bold">
                                {t('goalGame.guest.menuBye')}
                            </button>
                            {error && <p className="mt-2 text-sm text-red-300" data-testid="goal-setup-error">{error}</p>}
                        </div>
                    ) : (
                        /* STEP 1 — email, PIN, consent. */
                        <form onSubmit={starOffered ? onStarNext : onSetupSubmit}
                              className="space-y-3 text-left" data-testid="goal-setup-form">
                            <input type="email" required value={email} onChange={(e) => onEmailChange(e.target.value)}
                                   placeholder={t('goalGame.emailPlaceholder')} className={field}
                                   data-testid="goal-email-input" />
                            {/* THE SCREEN NAME, asked for right here — it is part
                                of the profile every player gets. */}
                            <input type="text" required value={screenName || ''} maxLength={24}
                                   onChange={(e) => onScreenNameChange?.(e.target.value)}
                                   placeholder={t('goalGame.player.namePlaceholder')}
                                   className={field} data-testid="goal-screen-name-input" />
                            <label className="block">
                                <span className="block text-xs opacity-60 mb-1">{t('goalGame.player.codeLabel')}</span>
                                <input type="tel" inputMode="numeric" autoComplete="off" maxLength={4}
                                       value={pin} onChange={(e) => onPinChange(e.target.value)}
                                       placeholder={t('goalGame.pinPlaceholder')}
                                       className={`${field} tracking-[0.4em] text-center`}
                                       data-testid="goal-pin-choose-input" />
                            </label>
                            <label className="flex items-start gap-2 text-xs opacity-80">
                                <input type="checkbox" checked={consent} onChange={(e) => onConsentChange(e.target.checked)}
                                       className="mt-0.5" data-testid="goal-consent-checkbox" />
                                <span>{t('goalGame.player.consent')}</span>
                            </label>
                            {/* One line about the live boards, in the same
                                consent block the player already reads. */}
                            <p className="text-[11px] opacity-60 -mt-1" data-testid="goal-consent-board">
                                {t('goalGame.player.boards')}
                            </p>
                            {/* Not part of the tick — the Law 25 line, kept small. */}
                            <p className="text-[11px] opacity-60 -mt-1" data-testid="goal-consent-note">
                                {t('goalGame.player.law')}
                            </p>
                            <button type="submit" disabled={joining} data-testid="goal-setup-submit"
                                    style={primaryBtn}
                                    className="w-full rounded-xl font-bold py-3 shadow-lg disabled:opacity-60">
                                {joining ? <Loader2 className="animate-spin mx-auto" size={20} />
                                    : (starOffered ? t('goalGame.next') : t('goalGame.setupDone'))}
                            </button>
                            {error && <p className="text-sm text-red-300" data-testid="goal-setup-error">{error}</p>}
                            {/* ALREADY A PLAYER — email and code, nothing else. */}
                            <button type="button" onClick={() => setFlow('signin')}
                                    data-testid="goal-already-player"
                                    className="w-full text-xs underline opacity-80 py-1">
                                {t('goalGame.player.haveCode')}
                            </button>
                        </form>
                    )}
                    {/* Staff can rename or clear the table at any time, with the
                        same code, right here. */}
                    {!state.table_number?.demo && tableLabel && (
                        <div className="mt-4 pt-3 border-t border-white/10">
                            <GoalStaffTable cardId={cardId} label={tableLabel}
                                            onDone={onCheckAgain}
                                            c={c} field={field} primaryBtn={primaryBtn}
                                            ghostBtn={ghostBtn} />
                        </div>
                    )}
                </div>
            </Shell>
        );
    }

    // ── the table already claimed this goal (the PIN identifies the table)
    if (alreadyPlayed || outcome?.already) {
        return (
            <Shell {...shellProps}>
                <div className={panel} style={panelCss} data-testid="goal-already-played">
                    <p className="text-lg font-bold mb-2" style={ty.headline}>{txt('tablePlayedTitle')}</p>
                    <p className="text-sm opacity-60">{t('goalGame.tablePlayedBody')}</p>
                    {state?.result === 'win' && (
                        <p className="mt-4 font-semibold" style={{ color: c.accent }} data-testid="goal-already-prize">
                            {t('goalGame.youWon')}: {prizeTitle(state.prize)}
                            {state.voucher?.code && (
                                <span className="block text-sm font-mono tracking-[0.18em] opacity-90"
                                      data-testid="goal-already-voucher">{state.voucher.code}</span>
                            )}
                            {state.voucher?.claim_required && (
                                <span className="block text-xs font-normal opacity-80"
                                      data-testid="goal-already-claim">
                                    {t('goalGame.reminders.claimLine')}
                                </span>
                            )}
                            {nextVisit && (
                                <span className="block text-xs font-normal opacity-80"
                                      data-testid="goal-already-next-visit">
                                    {validLine}
                                </span>
                            )}
                        </p>
                    )}
                    {/* The table's round, not necessarily this player's card:
                        nothing here promises THEM an email. */}
                    {myExtra}
                </div>
            </Shell>
        );
    }

    // ── result of the scratch just finished

    if (outcome && !outcome.closed) {
        return (
            <Shell {...shellProps}>
                <div className={`relative overflow-hidden rounded-2xl p-8 text-center border`}
                     style={won
                         ? { backgroundColor: 'rgba(255,255,255,0.09)', borderColor: pc.button,
                             boxShadow: `0 0 48px ${pc.button}55` }
                         : { backgroundColor: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.12)' }}
                     data-testid={won ? 'goal-result-win' : 'goal-result-lose'}>
                    {won && celebrate && <GoalCelebrate accent={pc.accent} button={pc.button} />}
                    <p className="relative font-black mb-3"
                       data-testid={won ? 'goal-win-title' : 'goal-lose-title'}
                       style={won
                           ? { ...pty.headline, fontSize: pty.headline.fontSize || undefined,
                               color: pc.button, textShadow: `0 0 28px ${pc.button}66` }
                           : ty.headline}>
                        {won ? txt('winTitle', 'revealTitle') : txt('loseTitle')}
                    </p>
                    {won
                        ? <>
                            {outcome.prize?.image_url && (
                                <img src={sizedUrl(outcome.prize.image_url, 440)} alt=""
                                     className="relative mx-auto mb-4 max-h-40 w-auto rounded-xl object-contain"
                                     data-testid="goal-prize-image" />
                            )}
                            <p className="relative text-2xl font-extrabold" style={{ color: pc.accent }} data-testid="goal-prize-title">{prizeTitle(outcome.prize)}</p>
                            {prizeWin(outcome.prize) && (
                                <p className="mt-2 text-sm opacity-80" data-testid="goal-prize-message">{prizeWin(outcome.prize)}</p>
                            )}
                            {nextVisit && (
                                /* A prize for the NEXT visit: it cannot be used
                                   tonight, and it has been emailed to the table. */
                                <div className="mt-3 rounded-2xl border border-white/20 bg-black/20 px-4 py-2"
                                     data-testid="goal-next-visit">
                                    <p className="text-sm font-bold" style={{ color: pc.accent }}
                                       data-testid="goal-next-visit-badge">
                                        {t('goalGame.nextVisitBadge')}
                                    </p>
                                    <p className="text-sm" data-testid="goal-next-visit-valid">
                                        {validLine}
                                    </p>
                                    {/* Said once: when the card block below is
                                        drawn it carries the email line. */}
                                    {!(outcome.voucher || state?.voucher) && (emailLine || (
                                        <p className="text-[11px] opacity-70" data-testid="goal-next-visit-emailed">
                                            {mineNotTable ? t('goalGame.emailedYou')
                                                : t('goalGame.nextVisitEmailed')}
                                        </p>
                                    ))}
                                </div>
                            )}
                            {(outcome.voucher || state?.voucher) && (
                                /* The real voucher. The code is what the bar
                                   scans or types in to redeem it. */
                                <div className="mt-4 rounded-2xl border border-white/20 bg-black/30 px-4 py-3"
                                     data-testid="goal-voucher">
                                    <p className="text-[10px] font-semibold uppercase tracking-wider opacity-60">
                                        {t('goalGame.voucherLabel')}
                                    </p>
                                    <p className="text-xl font-black tracking-[0.18em] font-mono"
                                       style={{ color: pc.accent }} data-testid="goal-voucher-code">
                                        {(outcome.voucher || state.voucher).code}
                                    </p>
                                    <p className="text-[11px] opacity-70">
                                        {(outcome.voucher || state.voucher).claim_required
                                            ? t('goalGame.reminders.claimLine')
                                            : t('goalGame.voucherHint')}
                                    </p>
                                    {/* Only ever promise an email that really
                                        went; otherwise say so and offer it
                                        again. */}
                                    {emailLine}
                                </div>
                            )}
                            {override('winSubtitle') && (
                                <p className="mt-3 text-sm opacity-60" data-testid="goal-win-subtitle">{override('winSubtitle')}</p>
                            )}
                          </>
                        : <p className="text-sm opacity-60">{txt('loseBody')}</p>}
                    <p className="mt-6 text-xs opacity-40">{t('goalGame.demoNote')}</p>
                </div>
            </Shell>
        );
    }

    // ── a round is OPEN
    if (open) {
        // The PIN is asked on every goal, on every phone at the table.
        if (!pinToken) {
            return (
                <Shell {...shellProps}>
                    <div className={pad}>
                        {state.event.goal_light && <GoalBeacon />}
                        <div className={panel} style={panelCss}>
                            <p className="text-2xl font-black tracking-tight mb-1" style={{ ...ty.headline, color: c.button }}
                               data-testid="goal-banner">{txt('pinEnterTitle')}</p>
                            <p className="text-4xl font-black tabular-nums my-3" data-testid="goal-countdown">{clock(left)}</p>
                            <p className="text-xs uppercase tracking-widest opacity-40 -mt-2 mb-1"
                               data-testid="goal-countdown-label">{t('goalGame.timeLeftToPlay')}</p>
                            <form onSubmit={onPinSubmit} className="space-y-3" data-testid="goal-pin-form">
                                <input type="tel" inputMode="numeric" autoComplete="off" maxLength={4}
                                       value={pin} onChange={(e) => onPinChange(e.target.value)}
                                       placeholder={t('goalGame.pinPlaceholder')}
                                       disabled={lockSeconds > 0}
                                       className={`${field} tracking-[0.4em] text-center`}
                                       data-testid="goal-pin-input" />
                                <button type="submit" disabled={joining || lockSeconds > 0}
                                        data-testid="goal-pin-submit"
                                        style={primaryBtn}
                                        className="w-full rounded-xl font-bold py-3 shadow-lg disabled:opacity-60">
                                    {joining ? <Loader2 className="animate-spin mx-auto" size={20} /> : t('goalGame.pinSubmit')}
                                </button>
                                {error && <p className="text-sm text-red-300" data-testid="goal-pin-error">{error}</p>}
                            </form>
                        </div>
                    </div>
                </Shell>
            );
        }

        return (
            <Shell {...shellProps}>
                <div className={pad}>
                    {state.event.goal_light && <GoalBeacon />}
                    <div className="text-center mb-5">
                        <p className="text-4xl font-black tracking-tight uppercase"
                           style={{ ...ty.headline, color: c.button, textShadow: `0 0 24px ${c.button}66` }}
                           data-testid="goal-banner">{override('goalBanner')
                               || t(brand?.league && brand.league !== 'nhl'
                                   ? 'goalGame.scoreBanner' : 'goalGame.goalBanner')}</p>
                        <p className="text-sm opacity-60 mt-1">{t('goalGame.scratchNow')}</p>
                        <p className="text-5xl font-black tabular-nums mt-3" data-testid="goal-countdown">{clock(left)}</p>
                        <p className="text-xs uppercase tracking-widest opacity-40"
                           data-testid="goal-countdown-label">{t('goalGame.timeLeftToPlay')}</p>
                    </div>
                    <div className="relative h-44 rounded-2xl overflow-hidden bg-slate-800 border border-white/10"
                         data-testid="goal-scratch-area">
                        <div className="absolute inset-0 flex items-center justify-center text-center px-6">
                            <p className="text-sm opacity-70">{t('goalGame.scratchHidden')}</p>
                        </div>
                        <GoalScratch
                            onComplete={onScratched}
                            label={(brand?.gift_copy?.[lang] || {}).scratchInstruction || t('goalGame.scratchHint')}
                            logoUrl={absUrl(brand?.scratch_logo_url)}
                            logoOpacity={brand?.scratch_logo_opacity}
                            logoSize={brand?.scratch_logo_size}
                        />
                    </div>
                </div>
            </Shell>
        );
    }

    // ── a round JUST closed (and this table did not win it)
    if (state.event && !won && (state.event.closed_ago ?? 0) <= 120) {
        return (
            <Shell {...shellProps}>
                {atMain && mainPicks}
                <div className="mt-3 rounded-2xl bg-white/5 border border-white/10 p-8 text-center"
                     data-testid="goal-missed">
                    <p className="text-lg font-bold mb-2" style={ty.headline}>{txt('missedTitle')}</p>
                    <p className="text-sm opacity-60">{txt('missedBody')}</p>
                    <button onClick={onCheckAgain} disabled={checking} data-testid="goal-check-again"
                            style={ghostBtn}
                            className="mt-5 inline-flex items-center gap-2 rounded-xl border border-white/25 px-4 py-2 text-sm font-semibold disabled:opacity-50">
                        <RotateCw size={14} className={checking ? 'animate-spin' : ''} />
                        {t('goalGame.checkAgain')}
                    </button>
                </div>
            </Shell>
        );
    }

    // ── nothing has happened yet
    return (
        <Shell {...shellProps}>
            {atMain && mainPicks}
            <div className="mt-3 rounded-2xl bg-white/5 border border-white/10 p-8 text-center"
                 data-testid="goal-no-round">
                <p className="text-base font-semibold mb-2" style={ty.headline}>{txt('noRoundTitle')}</p>
                <p className="text-sm opacity-60">{txt('noRoundBody')}</p>
                <button onClick={onCheckAgain} disabled={checking} data-testid="goal-check-again"
                        style={ghostBtn}
                        className="mt-5 inline-flex items-center gap-2 rounded-xl border border-white/25 px-4 py-2 text-sm font-semibold disabled:opacity-50">
                    <RotateCw size={14} className={checking ? 'animate-spin' : ''} />
                    {t('goalGame.checkAgain')}
                </button>
            </div>
        </Shell>
    );
};

// The branded page frame — venue logo, wallpaper and its darkening wash, the
// language toggle — reused by the Play from anywhere screen so a player at
// home sees the same venue as a fan in the room. Behaviour unchanged.
export { Shell as BrandShell };

export default GoalFanView;
